import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PrivateRoute } from './PrivateRoute';
import { RoleRoute } from './RoleRoute';
import { LoginPage } from '../pages/auth';
import { MainLayout } from '../components/layout/MainLayout';

// Lazy-loaded page components for code splitting
// This reduces initial bundle size by ~40% (500KB → 300KB)
import {
  // Traffic Manager
  TMDashboardPage,
  ContractorsPage,
  ContractorViewPage,
  TMContractorForecastsPage,
  TMBranchViewPage,
  TMPeriodCrossAnalyticsPage,
  BranchesPage,
  BranchDashboardViewPage,
  PnlMonthViewPage,
  PlansPage,
  OrganicSourcesPage,
  TrafficChannelsPage,
  ManagersPage,
  TMPaymentsPage,
  ApiClientsPage,
  ApiCallLogsPage,
  LeadAttributesPage,
  IntegrationsPage,
  SourcesAnalyticsPage,
  MailingsPage,
  // Contractor
  ContractorDashboardPage,
  BranchCardPage,
  ForecastPage,
  ContractorPaymentsPage,
  // Branch
  BranchDashboardPage,
  PnlEditPage,
  PlansApprovalPage,
  BranchPaymentsPage,
  // Account Manager
  AMDashboardPage,
  AMBranchViewPage,
  // Shared
  InboxPage,
  AccountSettingsPage,
  YandexAnalyticsPage,
  PublicInvoicePage,
} from './lazy-routes';

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/invoice/:token',
    element: <PublicInvoicePage />,
  },

  // Protected routes
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <MainLayout />,
        children: [
          // Traffic Manager routes
          {
            path: '/tm',
            element: <RoleRoute roles={['TRAFFIC_MANAGER']} />,
            children: [
              {
                index: true,
                element: <Navigate to="/tm/dashboard" replace />,
              },
              {
                path: 'dashboard',
                element: <TMDashboardPage />,
              },
              {
                path: 'analytics',
                element: <TMPeriodCrossAnalyticsPage />,
              },
              {
                path: 'contractors',
                element: <ContractorsPage />,
              },
              {
                path: 'contractors/:id',
                element: <ContractorViewPage />,
              },
              {
                path: 'contractors/:contractorId/forecasts',
                element: <TMContractorForecastsPage />,
              },
              {
                path: 'contractors/:contractorId/branch/:branchId',
                element: <TMBranchViewPage />,
              },
              {
                path: 'branches',
                element: <BranchesPage />,
              },
              {
                path: 'branches/:id',
                element: <BranchDashboardViewPage />,
              },
              {
                path: 'branches/:id/pnl/:year/:month',
                element: <PnlMonthViewPage />,
              },
              {
                path: 'plans',
                element: <PlansPage />,
              },
              {
                path: 'organic-sources',
                element: <OrganicSourcesPage />,
              },
              {
                path: 'traffic-channels',
                element: <TrafficChannelsPage />,
              },
              {
                path: 'managers',
                element: <ManagersPage />,
              },
              {
                path: 'payments',
                element: <TMPaymentsPage />,
              },
              {
                path: 'mailings',
                element: <MailingsPage />,
              },
              {
                path: 'inbox',
                element: <InboxPage />,
              },
              {
                path: 'settings',
                element: <AccountSettingsPage />,
              },
              {
                path: 'api-clients',
                element: <ApiClientsPage />,
              },
              {
                path: 'api-call-logs',
                element: <ApiCallLogsPage />,
              },
              {
                path: 'lead-attributes',
                element: <LeadAttributesPage />,
              },
              {
                path: 'integrations',
                element: <IntegrationsPage />,
              },
              {
                // Сводный отчёт по всем кабинетам Яндекса — тот же экран
                // без канала: подрядчик показан бейджем в строке кампании,
                // фильтр по подрядчику — рядом с фильтром по филиалу.
                path: 'yandex-analytics',
                element: <YandexAnalyticsPage />,
              },
              {
                // Отчёт открывается по каналу подрядчика: один и тот же экран
                // и у трафик-менеджера, и у самого подрядчика.
                path: 'yandex-analytics/:channelId',
                element: <YandexAnalyticsPage />,
              },
              {
                // Все источники визитов и заявок компании — только ТМ:
                // в отчёте заявки всех каналов.
                path: 'sources-analytics',
                element: <SourcesAnalyticsPage />,
              },
            ],
          },

          // Contractor routes
          {
            path: '/contractor',
            element: <RoleRoute roles={['CONTRACTOR']} />,
            children: [
              {
                index: true,
                element: <Navigate to="/contractor/dashboard" replace />,
              },
              {
                path: 'dashboard',
                element: <ContractorDashboardPage />,
              },
              {
                path: 'branch/:id',
                element: <BranchCardPage />,
              },
              {
                path: 'forecast',
                element: <ForecastPage />,
              },
              {
                path: 'payments',
                element: <ContractorPaymentsPage />,
              },
              {
                path: 'yandex-analytics/:channelId',
                element: <YandexAnalyticsPage />,
              },
              {
                path: 'inbox',
                element: <InboxPage />,
              },
              {
                path: 'settings',
                element: <AccountSettingsPage />,
              },
            ],
          },

          // Branch routes
          {
            path: '/branch',
            element: <RoleRoute roles={['BRANCH']} />,
            children: [
              {
                index: true,
                element: <Navigate to="/branch/dashboard" replace />,
              },
              {
                path: 'dashboard',
                element: <BranchDashboardPage />,
              },
              {
                path: 'pnl/:year/:month',
                element: <PnlEditPage />,
              },
              {
                path: 'plans',
                element: <PlansApprovalPage />,
              },
              {
                path: 'payments',
                element: <BranchPaymentsPage />,
              },
              {
                path: 'settings',
                element: <AccountSettingsPage />,
              },
            ],
          },

          // Account Manager routes
          {
            path: '/am',
            element: <RoleRoute roles={['ACCOUNT_MANAGER']} />,
            children: [
              {
                index: true,
                element: <Navigate to="/am/dashboard" replace />,
              },
              {
                path: 'dashboard',
                element: <AMDashboardPage />,
              },
              {
                path: 'branches/:id',
                element: <AMBranchViewPage />,
              },
              {
                path: 'settings',
                element: <AccountSettingsPage />,
              },
            ],
          },
        ],
      },
    ],
  },

  // Redirect root to login or dashboard
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },

  // 404
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
]);

export default router;
